import { useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useState } from "react";

import { SCOUT_INFO_PANEL_TABS, type ScoutInfoPanelTab } from "./scoutInfoPanelContent";

export type ScoutSidePanelView = "search" | "home" | ScoutInfoPanelTab;

export type ScoutSidePanelNonSearchView = Exclude<ScoutSidePanelView, "search">;

const DEFAULT_VIEW: ScoutSidePanelNonSearchView = "home";

function isInfoView(view: ScoutSidePanelView): view is ScoutInfoPanelTab {
  return SCOUT_INFO_PANEL_TABS.some((tab) => tab.value === view);
}

/**
 * Manages Scout's right-side icon rail and drawer across breakpoints.
 *
 * - Closed by default; the rail stays visible with icon shortcuts.
 * - `sm` and up: drawer sits beside the chat when open (persistent layout).
 * - Below `sm` (mobile): drawer opens as a temporary fullscreen overlay.
 *
 * The drawer shows the home zero state (recents, no tab selected), an info view
 * (Sources / Method / Privacy & Data, with chips), or the search view.
 * `lastNonSearchView` remembers which view to return to when the search
 * field's back affordance is used.
 *
 * Closing always resets to the home zero state, so the panel reopens fresh
 * (search field + unselected tabs + recents) rather than to whatever the user
 * was last looking at.
 */
export function useScoutInfoPanel() {
  const theme = useTheme();
  const isPersistentMode = useMediaQuery(theme.breakpoints.up("sm"));
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ScoutSidePanelView>(DEFAULT_VIEW);
  const [tab, setTab] = useState<ScoutInfoPanelTab | null>(null);
  const [lastNonSearchView, setLastNonSearchView] =
    useState<ScoutSidePanelNonSearchView>(DEFAULT_VIEW);

  const openToView = useCallback((nextView: ScoutSidePanelView) => {
    setView(nextView);
    if (isInfoView(nextView)) {
      setTab(nextView);
      setLastNonSearchView(nextView);
    } else if (nextView === "home") {
      setTab(null);
      setLastNonSearchView("home");
    }
    setOpen(true);
  }, []);

  const changeTab = useCallback(
    (nextTab: ScoutInfoPanelTab) => {
      if (tab === nextTab) {
        setTab(null);
        setView("home");
        setLastNonSearchView("home");
        return;
      }

      setTab(nextTab);
      setView(nextTab);
      setLastNonSearchView(nextTab);
    },
    [tab]
  );

  const closePanel = useCallback(() => {
    setOpen(false);
    setView(DEFAULT_VIEW);
    setTab(null);
    setLastNonSearchView(DEFAULT_VIEW);
  }, []);

  const togglePanel = useCallback(() => {
    if (open) {
      closePanel();
      return;
    }
    setOpen(true);
  }, [closePanel, open]);

  // From the search view, return to whichever non-search view the user came from.
  const backFromSearch = useCallback(() => {
    setView(lastNonSearchView);
    if (lastNonSearchView === "home") {
      setTab(null);
    } else {
      setTab(lastNonSearchView);
    }
  }, [lastNonSearchView]);

  return {
    open,
    view,
    tab,
    changeTab,
    isPersistentMode,
    openToView,
    togglePanel,
    backFromSearch,
    closePanel,
  };
}
